import { format, isValid, parseISO } from 'date-fns';

/**
 * Date formatting for every performance chart, in one place so the axis and the
 * tooltip can't drift apart (NEA-6380).
 *
 * Charts keep the raw `YYYY-MM-DD` on the data point and format at render time —
 * pre-baking the label into the series throws the real date away, which is what
 * stopped the tooltips from being able to name the previous period's date.
 */
const toDate = (value) => {
	if (value instanceof Date) return isValid(value) ? value : null;
	if (typeof value === 'number') {
		const fromEpoch = new Date(value);
		return isValid(fromEpoch) ? fromEpoch : null;
	}
	if (typeof value !== 'string' || !value) return null;
	const parsed = parseISO(value);
	return isValid(parsed) ? parsed : null;
};

/**
 * What to render when a value can't be read as a date. A string is passed
 * through (recharts hands the formatter whatever is on the datum, and an
 * already-formatted label should survive); anything else becomes empty, because
 * returning the value itself would put `Invalid Date` — or a raw object — in
 * front of a retailer.
 */
const passThrough = (value) => (typeof value === 'string' ? value : '');

/** Axis ticks: "1 Nov". No year — it would not fit, and the tooltip carries it. */
export const axisDateLabel = (value) => {
	const date = toDate(value);
	return date ? format(date, 'd MMM') : passThrough(value);
};

/** Tooltips: "1 Nov 2025". The year matters once a range can span 12 months. */
export const tooltipDateLabel = (value) => {
	const date = toDate(value);
	return date ? format(date, 'd MMM yyyy') : passThrough(value);
};

/**
 * Period-over-period change for a single point, as a signed percentage string.
 * Null when there is no baseline to compare against — "+100%" against nothing
 * is misleading, same rule the KPI pills follow.
 */
export const pointChangeLabel = (value, previous) => {
	// `Number(null)` is 0, so coercing first would read a missing day as a real
	// zero and report "-100%" for a day we simply have no figure for. A gap and
	// a genuine zero are different things on these charts.
	if (value == null || previous == null) return null;
	const current = Number(value);
	const before = Number(previous);
	if (!Number.isFinite(current) || !Number.isFinite(before) || before === 0) return null;
	const change = ((current - before) / before) * 100;
	if (!Number.isFinite(change)) return null;
	return `${change > 0 ? '+' : ''}${change.toFixed(1)}%`;
};
